/**
 * Build and print a self-contained, branded document for a single assistant
 * answer (text + graphs + references + AI disclaimer).
 *
 * jarvis-mmrag renders many bubbles on one page and charts as separate
 * Highcharts components, so rather than `window.print()` on the live page we
 * assemble an isolated HTML document and print it from a hidden iframe. The
 * graphs are captured as the already-rendered SVGs (no Highcharts `exporting`
 * module dependency); a `viewBox` is injected when absent so they scale to the
 * page width.
 */

import type { Citation } from '@/types'
import { getRiskColor, getRiskLabel } from './riskTier'

const LOGO_SRC = '/horizontal-logo.png'

const DISCLAIMER =
  'The information herein is provided under the terms of a paid subscription ' +
  'agreement with FORESIGHT Reports (www.foresightreports.com). This output was ' +
  'generated by an AI agent; it may contain inconsistencies. Redistribution ' +
  'outside of your organization requires consent; please contact us at ' +
  'general@foresightreports.com for further details.'

const PRINT_STYLES = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    color: #1f2933;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    font-size: 12pt;
    line-height: 1.55;
  }
  .print-page { padding: 24px 28px; }
  .print-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }
  .print-header img { max-width: 10rem; max-height: 48px; object-fit: contain; }
  .print-header .generated { font-size: 9pt; color: #6b7280; font-style: italic; }
  .print-body h1 { font-size: 18pt; }
  .print-body h2 { font-size: 15pt; }
  .print-body h3 { font-size: 13pt; }
  .print-body h1, .print-body h2, .print-body h3, .print-body h4 {
    margin: 1.1em 0 0.5em;
    font-weight: 700;
    line-height: 1.25;
  }
  .print-body p { margin: 0.6em 0; }
  .print-body ul, .print-body ol { margin: 0.6em 0; padding-left: 1.5em; }
  .print-body li { margin: 0.25em 0; }
  .print-body a { color: #1d4ed8; text-decoration: none; }
  .print-body code {
    font-family: "SF Mono", Menlo, Consolas, monospace;
    font-size: 0.9em;
    background: #f3f4f6;
    padding: 0.1em 0.3em;
    border-radius: 3px;
  }
  .print-body pre {
    background: #f3f4f6;
    padding: 10px 12px;
    border-radius: 6px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .print-body pre code { background: none; padding: 0; }
  .print-body table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.8em 0;
    font-size: 0.92em;
  }
  .print-body th, .print-body td {
    border: 1px solid #d1d5db;
    padding: 5px 8px;
    text-align: left;
    vertical-align: top;
  }
  .print-body th { background: #f3f4f6; font-weight: 600; }
  .print-body sup.cite-ref { color: #1d4ed8; font-weight: 600; font-size: 0.7em; }
  .print-charts { margin-top: 18px; }
  .print-chart {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px;
    margin: 12px 0;
    page-break-inside: avoid;
  }
  .print-chart svg { max-width: 100%; height: auto; display: block; }
  .print-references {
    margin-top: 22px;
    border-top: 1px solid #e5e7eb;
    padding-top: 12px;
  }
  .print-references h2 {
    font-size: 11pt;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin: 0 0 8px;
  }
  .print-references ol { margin: 0; padding-left: 1.4em; }
  .print-references li { margin: 0.35em 0; page-break-inside: avoid; }
  .print-references .ref-risk {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.85em;
    font-weight: 600;
    margin-right: 6px;
  }
  .print-references .ref-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .print-references a { color: #1d4ed8; text-decoration: none; word-break: break-word; }
  .print-disclaimer {
    margin-top: 28px;
    padding-top: 10px;
    text-align: center;
    font-size: 8.5pt;
    font-style: italic;
    color: #6b6b6b;
    border-top: 1px solid #e5e7eb;
    page-break-inside: avoid;
  }
  @page { size: A4; margin: 12mm 10mm; }
`

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function isLinkable(url?: string | null): boolean {
  return !!url && /^https?:\/\//i.test(url.trim())
}

/**
 * Serialize a live Highcharts `<svg>` to a standalone string, injecting a
 * `viewBox` from its width/height attributes when missing so it scales cleanly.
 */
export function serializeChartSvg(svg: SVGElement): string {
  const clone = svg.cloneNode(true) as SVGElement
  if (!clone.getAttribute('viewBox')) {
    const width = parseFloat(clone.getAttribute('width') || '')
    const height = parseFloat(clone.getAttribute('height') || '')
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      clone.setAttribute('viewBox', `0 0 ${width} ${height}`)
    }
  }
  if (!clone.getAttribute('xmlns')) {
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  }
  return clone.outerHTML
}

/** Numbered references list with a risk-tier dot + label and linked title. */
export function buildReferencesHtml(citations: Citation[]): string {
  if (!citations.length) return ''
  const items = citations
    .map((c) => {
      const title = escapeHtml(c.title || c.url || `Reference ${c.id}`)
      const titleHtml = isLinkable(c.url)
        ? `<a href="${escapeHtml(c.url)}" target="_blank" rel="noopener noreferrer">${title}</a>`
        : title
      const risk =
        c.riskTier != null
          ? `<span class="ref-risk" style="color:${getRiskColor(c.riskTier)}"><span class="ref-dot" style="background:${getRiskColor(c.riskTier)}"></span>${escapeHtml(getRiskLabel(c.riskTier))}</span>`
          : ''
      return `<li value="${c.id}">${risk}${titleHtml}</li>`
    })
    .join('')
  return `<section class="print-references"><h2>References</h2><ol>${items}</ol></section>`
}

interface PrintArgs {
  bodyHtml: string
  chartSvgs: string[]
  citations: Citation[]
}

/** Assemble the full standalone print document. */
export function buildPrintHtml({ bodyHtml, chartSvgs, citations }: PrintArgs): string {
  const generated = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const chartsHtml = chartSvgs.length
    ? `<div class="print-charts">${chartSvgs
        .map((svg) => `<div class="print-chart">${svg}</div>`)
        .join('')}</div>`
    : ''
  const referencesHtml = buildReferencesHtml(citations)

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>FORESIGHT Smart Assist</title>
<style>${PRINT_STYLES}</style>
</head>
<body>
<div class="print-page">
  <header class="print-header">
    <img src="${LOGO_SRC}" alt="FORESIGHT" />
    <span class="generated">Generated ${escapeHtml(generated)}</span>
  </header>
  <main class="print-body">${bodyHtml}</main>
  ${chartsHtml}
  ${referencesHtml}
  <footer class="print-disclaimer">${escapeHtml(DISCLAIMER)}</footer>
</div>
</body>
</html>`
}

/**
 * Render the print document into an off-screen iframe and trigger the browser
 * print dialog. Waits for the logo image to settle so it is included, then
 * removes the iframe once printing is dismissed.
 */
export function printAnswer(args: PrintArgs): void {
  const html = buildPrintHtml(args)
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.style.visibility = 'hidden'
  document.body.appendChild(iframe)

  const cleanup = () => {
    // Defer removal so the print dialog has finished reading the document.
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    }, 1000)
  }

  iframe.onload = () => {
    const win = iframe.contentWindow
    if (!win) {
      cleanup()
      return
    }
    const triggerPrint = () => {
      try {
        win.focus()
        win.print()
      } catch (e) {
        console.error('[print] failed to print:', e)
      } finally {
        cleanup()
      }
    }
    // Ensure the logo image is loaded before printing so it isn't dropped.
    const img = win.document.querySelector('img')
    if (img && !img.complete) {
      let done = false
      const go = () => {
        if (done) return
        done = true
        triggerPrint()
      }
      img.addEventListener('load', go)
      img.addEventListener('error', go)
      setTimeout(go, 1500)
    } else {
      triggerPrint()
    }
  }

  const doc = iframe.contentWindow?.document
  if (!doc) {
    cleanup()
    return
  }
  doc.open()
  doc.write(html)
  doc.close()
}
